import { fathomVariables } from "@store/fast-versions";
import {
  IconAlignJustified,
  IconBox,
  IconBuilding,
  IconCalendarCog,
  IconCalendarTime,
  IconPalette,
  IconPhoto,
  IconQuote,
  IconSignature,
  IconSparkles,
  IconUserDown,
  IconUserUp,
  IconWorld,
} from "@tabler/icons-react";
import type { ComponentType } from "react";
import { requiredPlanForVariable } from "../plan-rules/plan-rules";
import {
  STORYDOC_CATEGORIES,
  STORYDOC_VARIABLES,
} from "../data/storydoc-variables";
import type {
  PickerCategory,
  PickerTab,
  PickerTabContent,
  PickerVariable,
} from "./types";

type IconType = ComponentType<{ className?: string; stroke?: number }>;

// Presentation-only: category → leading icon. Data lives in the canonical module.
const CATEGORY_ICONS: Record<string, IconType> = {
  prospect: IconUserDown,
  company: IconBuilding,
  sender: IconUserUp,
  organization: IconWorld,
  "proposal-deal": IconSignature,
  product: IconBox,
  "ai-content": IconSparkles,
  media: IconPhoto,
  "social-proof": IconQuote,
  content: IconAlignJustified,
  "time-date": IconCalendarTime,
  "date-operations": IconCalendarCog,
  design: IconPalette,
};

export const buildStorydocCategories = (): PickerCategory[] =>
  STORYDOC_CATEGORIES.map((cat) => ({
    id: cat.id,
    label: cat.label,
    icon: CATEGORY_ICONS[cat.id],
    variables: STORYDOC_VARIABLES.filter((sv) => sv.category === cat.id).map(
      (sv): PickerVariable => ({
        id: sv.key,
        name: sv.label,
        token: `{{${sv.key}}}`,
        type: sv.type,
        // The crown follows the publish gate, not the catalogue alone: the
        // legacy and sender/org variables are free there whatever the
        // catalogue says, so a crown on them promised an upgrade publishing
        // never asks for.
        plan: requiredPlanForVariable({ name: sv.key, type: sv.type }),
      }),
    ),
  }));

const FATHOM_VARIABLES: PickerVariable[] = fathomVariables.map((f) => ({
  id: f.variable.name,
  name: f.variable.title,
  token: `{{${f.variable.name}}}`,
  type: "fathom",
}));

// `hubspot` (live hook) and `custom` (CustomTab + local store) are handled
// directly in VariablePicker, so they're intentionally absent here.
export const PICKER_CONTENT: Partial<Record<PickerTab, PickerTabContent>> = {
  storydoc: { kind: "categories", categories: buildStorydocCategories() },
  fathom: { kind: "flat", variables: FATHOM_VARIABLES },
};
